import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAppContext } from '@/context/Context';
import { encodeData } from '@/utils/encodeUtils';
import { EnrichmentPoint } from '../types';
import { trackEvent } from '@/utils/analytics';

export interface EnrichmentDataTableProps {
  data: EnrichmentPoint[];
  isDownload?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: string[]) => void;
  onDiseaseClick?: (item: EnrichmentPoint) => void;
  onIntersectionClick?: (item: EnrichmentPoint) => void;
  geneFilterType?: string;
}

function getGeneFilterName(geneFilterType?: string): string {
  switch (geneFilterType) {
    case 'geneId':
      return 'geneNcbiID';
    case 'ensemblId':
      return 'geneEnsemblIDs';
    case 'uniprotId':
      return 'geneProteinStrIDs';
    case 'geneSymbol':
    case 'name':
    default:
      return 'geneSymbol';
  }
}

function getDiseaseIdent(item: EnrichmentPoint): string | null {
  if (item.diseaseUMLSCUI?.trim()) return item.diseaseUMLSCUI.trim();
  if (item.diseaseName?.trim()) return item.diseaseName.trim();
  return null;
}

type SortColumn =
  | 'disease'
  | 'pvalue'
  | 'fdrQValue'
  | 'negLog10P'
  | 'bgRatio'
  | 'oddsRatio'
  | 'geneRatio'
  | 'intersection';

type SortDirection = 'asc' | 'desc';

type SortState = { column: SortColumn; direction: SortDirection };

const DEFAULT_SORT: SortState = { column: 'oddsRatio', direction: 'desc' };

function defaultDirectionForColumn(column: SortColumn): SortDirection {
  if (column === 'disease' || column === 'pvalue' || column === 'fdrQValue') return 'asc';
  return 'desc';
}

function parseGeneRatioForSort(s: string): number | null {
  const m = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/.exec(String(s).trim());
  if (!m) return null;
  const num = Number(m[1]);
  const den = Number(m[2]);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
  return num / den;
}

function compareByColumn(a: EnrichmentPoint, b: EnrichmentPoint, column: SortColumn): number {
  switch (column) {
    case 'disease':
      return a.diseaseName.localeCompare(b.diseaseName, undefined, { sensitivity: 'base' });
    case 'pvalue':
      return Number(a.pvalue) - Number(b.pvalue);
    case 'fdrQValue':
      return Number(a.fdrQValue ?? 1) - Number(b.fdrQValue ?? 1);
    case 'negLog10P':
      return -Math.log10(Number(a.pvalue)) - -Math.log10(Number(b.pvalue));
    case 'bgRatio':
      return Number(a.bgRatioNum) - Number(b.bgRatioNum);
    case 'oddsRatio':
      return Number(a.oddsRatioNum ?? 0) - Number(b.oddsRatioNum ?? 0);
    case 'geneRatio': {
      const fa = parseGeneRatioForSort(a.geneRatioValue);
      const fb = parseGeneRatioForSort(b.geneRatioValue);
      if (fa !== null && fb !== null) return fa - fb;
      return String(a.geneRatioValue).localeCompare(String(b.geneRatioValue), undefined, {
        numeric: true,
      });
    }
    case 'intersection':
      return a.intersectionSize - b.intersectionSize;
    default:
      return 0;
  }
}

const DataTable = ({
  data,
  isDownload = false,
  selectedIds,
  onSelectionChange,
  onDiseaseClick,
  onIntersectionClick,
  geneFilterType,
}: EnrichmentDataTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortState, setSortState] = useState<SortState>(DEFAULT_SORT);
  const itemsPerPage = 20;
  const context = userAppContext();
  const navigate = useNavigate();

  const handleSortColumn = useCallback((column: SortColumn) => {
    setCurrentPage(1);
    setSortState(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: defaultDirectionForColumn(column) };
    });
  }, []);

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => compareByColumn(a, b, sortState.column));
    return sortState.direction === 'asc' ? sorted : sorted.reverse();
  }, [data, sortState]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  const isInteractive =
    !isDownload && (onSelectionChange != null || onDiseaseClick != null || onIntersectionClick != null);
  const showCheckboxes = !isDownload && onSelectionChange != null && selectedIds != null;

  const selectedSet = selectedIds ?? new Set<string>();
  const allOnPageSelected = currentItems.length > 0 && currentItems.every(item => selectedSet.has(item.id));
  const someOnPageSelected = currentItems.some(item => selectedSet.has(item.id));
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
  }, [someOnPageSelected, allOnPageSelected]);

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allOnPageSelected) {
      const removed = new Set(currentItems.map(i => i.id));
      onSelectionChange([...selectedSet].filter(id => !removed.has(id)));
    } else {
      const addIds = currentItems.map(i => i.id);
      onSelectionChange([...new Set([...selectedSet, ...addIds])]);
    }
  }, [onSelectionChange, currentItems, selectedSet, allOnPageSelected]);

  const handleSelectRow = useCallback(
    (item: EnrichmentPoint) => {
      if (!onSelectionChange) return;
      const next = new Set(selectedSet);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      onSelectionChange([...next]);
    },
    [onSelectionChange, selectedSet],
  );

  const handleDiseaseClick = useCallback(
    (item: EnrichmentPoint) => {
      if (isDownload) return;
      trackEvent({
        name: 'navigation_click',
        buttonId: 'enrichment_table_disease_click',
        label: 'Open disease summary from enrichment',
        page: 'enrichment',
        section: 'results_table',
        feature: 'navigation',
        eventCategory: 'enrichment',
      });
      onDiseaseClick?.(item);
      const ctx = context as { restrictSources?: string | false } | null;
      const source = ctx?.restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      const isVariantEnrichment =
        typeof item.variantRatio === 'string' && item.variantRatio.trim().length > 0;
      const variants = item.intersection;

      if (isVariantEnrichment) {
        if (!Array.isArray(variants) || variants.length === 0) return;
        navigate({
          pathname: '/search',
          search: `?view=VARIANTS&idents=${variants.map(variant => encodeURIComponent(variant)).join('-')}&source=${source}&tab=VARIANTS`,
        });
        return;
      }

      const ident = getDiseaseIdent(item);
      if (!ident) return;
      navigate({
        pathname: '/search',
        search: `?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=GDA`,
      });
    },
    [isDownload, onDiseaseClick, context, navigate],
  );

  const handleIntersectionClick = useCallback(
    (item: EnrichmentPoint) => {
      if (isDownload) return;
      trackEvent({
        name: 'navigation_click',
        buttonId: 'enrichment_table_intersection_click',
        label: 'Open intersection summary from enrichment',
        page: 'enrichment',
        section: 'results_table',
        feature: 'navigation',
        eventCategory: 'enrichment',
      });
      onIntersectionClick?.(item);
      const ctx = context as { restrictSources?: string | false } | null;
      const source = ctx?.restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      const isVariantEnrichment =
        typeof item.variantRatio === 'string' && item.variantRatio.trim().length > 0;
      const variants = (item as EnrichmentPoint & { intersection?: string[] })?.intersection;

      if (isVariantEnrichment) {
        if (!Array.isArray(variants) || variants.length === 0) return;
        const ident = getDiseaseIdent(item);
        if (!ident) return;
        const filterSummary = [
          {
            name: 'variant',
            operator: 'eq' as const,
            type: 'string' as const,
            value: [...variants],
            isEvidence: false,
          },
        ];
        const fEncoded = encodeData(filterSummary);
        navigate({
          pathname: '/search',
          search: `?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=VDA&f=${fEncoded}`,
        });
        return;
      }

      const ident = getDiseaseIdent(item);
      if (!ident) return;

      const genes = (item as EnrichmentPoint & { intersection?: string[] })?.intersection;
      if (Array.isArray(genes) && genes.length > 0) {
        const filterSummary = [
          {
            name: getGeneFilterName(geneFilterType),
            operator: 'eq' as const,
            type: 'string' as const,
            value: [...genes],
            isEvidence: false,
          },
        ];
        const fEncoded = encodeData(filterSummary);
        navigate({
          pathname: '/search',
          search: `?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=GDA&f=${fEncoded}`,
        });
        return;
      }

      navigate({
        pathname: '/search',
        search: `?view=DISEASES&idents=${encodeURIComponent(ident)}&source=${source}&tab=GDA`,
      });
    },
    [isDownload, onIntersectionClick, context, navigate, geneFilterType],
  );

  const title = isDownload ? 'Top Most Significant Results' : '';
  const containerClass = isDownload
    ? 'bg-gray-50 p-4 rounded-lg border border-gray-300'
    : 'bg-gray-50 p-4 rounded-lg border border-gray-300 mt-4';

  const sortableHeader = (column: SortColumn, label: string, align: 'left' | 'right', isLast = false) => {
    const active = sortState.column === column;
    const ariaSort =
      active && sortState.direction === 'asc'
        ? 'ascending'
        : active && sortState.direction === 'desc'
          ? 'descending'
          : 'none';
    return (
      <th
        className={`${align === 'left' ? 'text-left' : 'text-right'} py-0 px-0 font-bold ${isLast ? '' : 'border-r border-gray-300'}`}
        aria-sort={ariaSort}
      >
        <button
          type="button"
          onClick={() => handleSortColumn(column)}
          className={`w-full py-2 px-3 font-bold flex items-center gap-1 min-h-[2.5rem] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${
            align === 'left' ? 'justify-start text-left' : 'justify-end text-right'
          }`}
          aria-label={`Ordenar por ${label}`}
        >
          <span className="text-xs">{label}</span>
          {active && (
            <span aria-hidden className="inline-block min-w-[1ch] text-xs">
              {sortState.direction === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </button>
      </th>
    );
  };

  return (
    <div className={containerClass}>
      {title && <h3 className="text-lg font-bold text-gray-700 mb-4 text-center">{title}</h3>}
      {!isDownload && showCheckboxes && (
        <p className="text-sm text-gray-600 mb-2">
          Select diseases to display in the graph above. Click a disease name or intersection to open the
          Disease Summary view.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" role="grid" aria-label="Enrichment results table">
          <thead>
            <tr className="border-b-2 border-gray-400 bg-gray-100">
              {showCheckboxes && (
                <th className="text-left py-2 px-3 font-bold border-r border-gray-300 w-10">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={handleSelectAll}
                      aria-label="Select all diseases on this page for graph"
                      className="rounded border-gray-300"
                    />
                    <span className="sr-only">Select all on page</span>
                  </label>
                </th>
              )}
              {sortableHeader('disease', 'Disease/Phenotype', 'left')}
              {sortableHeader('pvalue', 'p-value', 'right')}
              {sortableHeader('fdrQValue', 'FDR Q-value', 'left')}
              {sortableHeader('negLog10P', '-log10(p)', 'left')}
              {sortableHeader('bgRatio', 'bgRatio', 'right')}
              {sortableHeader('oddsRatio', 'oddsRatio', 'right')}
              {sortableHeader('geneRatio', 'Gene Ratio', 'right')}
              {sortableHeader('intersection', 'Intersection', 'right', true)}
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item, index) => {
              const ident = getDiseaseIdent(item);
              const canNavigate = !!ident;
              const diseaseClickable = isInteractive && canNavigate;
              const intersectionClickable = isInteractive && canNavigate;

              return (
                <tr
                  key={item.id}
                  className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  {showCheckboxes && (
                    <td className="py-2 px-3 border-r border-gray-200">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSet.has(item.id)}
                          onChange={() => handleSelectRow(item)}
                          aria-label={`Select ${item.diseaseName} for graph`}
                          className="rounded border-gray-300"
                        />
                      </label>
                    </td>
                  )}
                  <td className="py-2 px-3 text-left border-r border-gray-200">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0 border border-gray-400"
                        style={{ backgroundColor: item.color }}
                        aria-hidden
                      />
                      {diseaseClickable ? (
                        <button
                          type="button"
                          onClick={() => handleDiseaseClick(item)}
                          data-demo-tour="enrichment-disease-button"
                          className="truncate max-w-[250px] text-left text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-0.5"
                          title={`View Disease Summary: ${item.diseaseName}`}
                        >
                          {item.diseaseName.length > 40
                            ? `${item.diseaseName.substring(0, 40)}...`
                            : item.diseaseName}
                        </button>
                      ) : (
                        <span className="truncate max-w-[250px]" title={item.diseaseName}>
                          {item.diseaseName.length > 40
                            ? `${item.diseaseName.substring(0, 40)}...`
                            : item.diseaseName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {Number(item.pvalue).toExponential(2)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {item.fdrQValue != null ? Number(item.fdrQValue).toExponential(2) : '—'}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {(-Math.log10(item.pvalue)).toFixed(2)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {isDownload ? Number(item.bgRatioNum).toFixed(4) : Number(item.bgRatioNum).toFixed(6)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {Number(item.oddsRatioNum).toFixed(2)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono border-r border-gray-200">
                    {item.geneRatioValue}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">
                    {intersectionClickable ? (
                      <button
                        type="button"
                        onClick={() => handleIntersectionClick(item)}
                        data-demo-tour="enrichment-intersection-button"
                        className="text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-1"
                        title={`View Disease Summary: ${item.diseaseName}`}
                      >
                        {item.intersectionSize}
                      </button>
                    ) : (
                      item.intersectionSize
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="flex justify-center gap-2 mt-4" aria-label="Table pagination">
          <button
            type="button"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Previous page"
          >
            Prev
          </button>

          <span className="px-2 py-1" aria-live="polite">
            {currentPage} / {totalPages}
          </span>

          <button
            type="button"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Next page"
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
};

export default DataTable;
